import { useEffect, useMemo, useState } from 'react';
import { createMuiBrandTheme } from './muiTheme';
import {
  applyBrandTheme,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
} from './brandTheme';

/**
 * Reactive MUI theme hook that listens to API-driven brandTheme changes.
 * Watches localStorage + CSS variables + storage events.
 * Isolated — does not re-trigger global applyBrandTheme unless setter is used.
 */
export const useMuiBrandTheme = () => {
  const readColors = () => ({
    primaryColor:
      localStorage.getItem('primaryColor') ||
      localStorage.getItem('brandPrimaryColor') ||
      DEFAULT_PRIMARY_COLOR,
    secondaryColor:
      localStorage.getItem('secondaryColor') ||
      localStorage.getItem('brandSecondaryColor') ||
      DEFAULT_SECONDARY_COLOR,
  });

  const [colors, setColors] = useState<MuiBrandColors>(readColors);

  useEffect(() => {
    const sync = () => setColors(readColors());
    // storage event (other tabs) + custom event from applyBrandTheme callers
    window.addEventListener('storage', sync);
    window.addEventListener('brandThemeChange', sync as EventListener);
    // MutationObserver on :root style changes as fallback
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('brandThemeChange', sync as EventListener);
      obs.disconnect();
    };
  }, []);

  const theme = useMemo(
    () => createMuiBrandTheme({ primaryColor: colors.primaryColor, secondaryColor: colors.secondaryColor }),
    [colors.primaryColor, colors.secondaryColor],
  );

  const setBrandColors = (next: Partial<MuiBrandColors>) => {
    const merged = {
      primaryColor: next.primaryColor ?? colors.primaryColor,
      secondaryColor: next.secondaryColor ?? colors.secondaryColor,
    };
    applyBrandTheme(merged);
    window.dispatchEvent(new CustomEvent('brandThemeChange'));
    setColors({ primaryColor: merged.primaryColor!, secondaryColor: merged.secondaryColor! });
  };

  return { theme, colors, setBrandColors, readColors };
};

type MuiBrandColors = { primaryColor: string; secondaryColor: string };
